import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import {
  ErrorFallback,
  Loader,
  MessageAlert,
  DropDownInput,
} from 'smart-react';
import { Form } from '@progress/kendo-react-form';
import { getProjects, listVersions, syncProjects } from '../../Services/ProjectService';
import './SelectProjectForm.scss';
import { Button } from '@progress/kendo-react-buttons';
import { Label } from '@progress/kendo-react-labels';
const pageSize = 25;
const SelectProjectForm = () => {
  const [projectOptions, setProjectOptions] = React.useState([]);
  const [versionOptions, setVersionOptions] = React.useState([]);
  const [versionLoading, setVersionLoading] = React.useState(false);
  const [projectLoading, setProjectLoading] = React.useState(false);
  const [project, setProject] = React.useState(null);
  const [version, setVersion] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);

  const [projectTotal, setProjectTotal] = React.useState(0);
  const [projectPage, setProjectPage] = React.useState(1); // 1-based page index
  const [projectSearch, setProjectSearch] = React.useState('');
  const [projectOptionsSource, setProjectOptionsSource] = React.useState([]); // last server result for optimistic local filtering
  const mapToOption = (item) => ({ value: item.id, text: item.name });

  const [alertMessage, setAlertMessage] = React.useState({
    alert: false,
    message: null,
    type: 'danger',
  });
  const navigate = useNavigate();

  const showAlert = (type, message) => {
    setAlertMessage({ alert: true, message, type });
    setTimeout(() => {
      setAlertMessage((prev) =>
        prev.alert ? { ...prev, alert: false } : prev
      );
    }, 4000);
  };

  const loadProjects = async ({
    page = 1,
    search = projectSearch,
    append = false,
  } = {}) => {
    setProjectLoading(true);
    const res = await getProjects({ pageNumber: page, pageSize, search });
    setProjectLoading(false);

    if (res?.isSuccess) {
      const incoming = (res.projects || []).map(mapToOption);

      setProjectTotal(res.totalCount ?? 0);
      setProjectPage(page);

      if (append) {
        // Deduplicate by value when appending
        setProjectOptions((prev) => {
          const byId = new Map(prev.map((x) => [x.value, x]));
          for (const opt of incoming) byId.set(opt.value, opt);
          const deduped = Array.from(byId.values());
          setProjectOptionsSource(deduped);
          return deduped;
        });
      } else {
        setProjectOptions(incoming);
        setProjectOptionsSource(incoming);
      }
    } else {
      showAlert('danger', res?.message || 'Error fetching Projects!');
    }
  };
  const handleProjectChange = (event) => {
    const selectedValue = event.target.value.value;
    setProject(selectedValue);
    setVersion(null);
    setVersionOptions([]);
    // Exit early if the default item is selected
    if (!selectedValue) {
      return;
    }
    fetchVersion(selectedValue);
  };

  const handleVersionChange = (event) => {
    const value = event.target.value.value;
    setVersion(value);
  };

  const fetchVersion = async (projectKey) => {
    setVersionLoading(true);
    setVersionOptions([]);
    try {
      const data = await listVersions({ projectKey, isServiceWorker: false });
      if (data?.isSuccess) {
        const projects = data?.versions.map((item) => ({
          value: item.id,
          text: item.name,
        }));
        setVersionOptions(projects);
      } else {
        showAlert('danger', data?.message);
      }
    } catch (error) {
      console.log(error);
      showAlert('danger', 'Error fetching Version!');
    } finally {
      setVersionLoading(false);
    }
  };

  const handleCreateRollout = () => {
    if (project && version) {
      navigate(`/createrollout/${project}/${version}/new/0`);
    } else {
      showAlert('danger', 'Please Select Project & Version!');
    }
  };
  const debounceRef = React.useRef();

  const handleProjectFilterChange = (e) => {
    const value = e?.filter?.value ?? '';
    setProjectSearch(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Reset to first page when searching
      loadProjects({ page: 1, search: value, append: false });
    }, 300);
  };
  useEffect(() => {
    loadProjects({ page: 1, search: '' });
  }, []);

  // Lazy load: virtual paging
  const handleProjectPageChange = async (event) => {
    const { skip, take } = event; // Kendo-style virtual args
    const requestedPage = Math.floor((skip ?? 0) / (take || pageSize)) + 1;
    if (requestedPage !== projectPage) {
      await loadProjects({
        page: requestedPage,
        append: requestedPage > 1 && !projectSearch, // append when paginating the same search term
      });
    }
  };
  const virtualState = {
    total: projectTotal,
    skip: (projectPage - 1) * pageSize,
    pageSize,
  };

  // Optimistic local filter + debounced server search
  const onFilterChange = (event) => {
    const value = event?.filter?.value ?? '';

    // 1) Local immediate filter against last server result
    if (!value) {
      setProjectOptions(projectOptionsSource);
    } else {
      const v = String(value).toLowerCase();
      const locallyFiltered = projectOptionsSource.filter((opt) =>
        String(opt.text).toLowerCase().includes(v)
      );
      setProjectOptions(locallyFiltered);
    }

    // 2) Debounced remote search
    setProjectSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Reset to first page for new search term
      loadProjects({ page: 1, search: value, append: false });
    }, 300);
  };
  const pageChange = async (event) => {
    
    const requestedPage = projectPage + 1;
    setProjectPage(requestedPage);

    const allFetched =
      projectTotal > 0 && projectOptionsSource.length >= projectTotal;
    if (allFetched || projectLoading) {
      // All pages for current query are locally available; no server call.
      return;
    }

    await loadProjects({
      page: requestedPage,
      search: projectSearch,
      append: requestedPage > 1 && !projectSearch,
    });
  };

  const handleSyncProjects = async () => {
    try {
      setSyncing(true);
      const res = await syncProjects();
      if (!res?.isSuccess) {
        showAlert(
          'danger',
          res?.message || res?.status?.message || 'Sync failed'
        );
      } else {
        // Refresh first page for current search term
        await loadProjects({ page: 1, search: projectSearch, append: false });
        showAlert('success', 'Projects synced successfully');
      }
    } catch (e) {
      showAlert('danger', 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <React.Suspense fallback={<Loader />}>
        <div className="select-project-form add-page-section k-pl-1">
          {versionLoading && <Loader />}
          {alertMessage.alert && (
            <MessageAlert
              type={alertMessage.type}
              message={alertMessage.message}
              isIcon={true}
              iconName={alertMessage.type === 'success' ? 'check' : 'error'}
            />
          )}
          <div className={'detail-card'}>
            <div className={'detail-card-body'}>
              <Form
                render={() => (
                  <div
                    className="k-d-flex single-field-row single-row-100 align-items-center"
                    style={{ alignItems: 'center' }}
                  >
                    <div className="k-p-2 field-wrapper k-w-40">
                      <Label editorId="project">Select Project: </Label>
                      <div className="project-row">
                        <div className="project-dropdown">
                          <DropDownInput
                            dataSet={projectOptions}
                            textField="text"
                            dataItemKey="value"
                            onChange={handleProjectChange}
                            defaultItem={{
                              text: 'Select Project...',
                              value: '',
                              disabled: true,
                            }}
                            filterable={true}
                            required={true}
                            onFilterChange={onFilterChange}
                            virtual={virtualState}
                            onPageChange={pageChange}
                            loading={projectLoading}
                          />
                        </div>
                        <Button
                          className={`sync-btn ${syncing ? 'is-syncing' : ''}`}
                          themeColor="primary"
                          size="small"
                          fillMode="outline"
                          icon="refresh"
                          onClick={handleSyncProjects}
                          disabled={projectLoading || syncing}
                          aria-label="Sync projects"
                          title={syncing ? 'Syncing...' : 'Sync projects'}
                        />
                      </div>
                    </div>
                    {versionOptions.length > 0 && (
                      <div className="k-p-2 field-wrapper k-w-40">
                        <Label editorId="version">Select Version: </Label>
                        <div className="project-row">
                          <div className="project-dropdown">
                            <DropDownInput
                              dataSet={versionOptions}
                              textField="text"
                              dataItemKey="value"
                              defaultItem={{
                                text: 'Select Version...',
                                value: '',
                                disabled: true,
                              }}
                              onChange={handleVersionChange}
                              filterable={true}
                              required={true}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="k-p-2 field-wrapper create-btn-wrapper">
                      <Button
                        themeColor="primary"
                        className="k-mx-2"
                        size="medium"
                        onClick={handleCreateRollout}
                      >
                        Create Rollout
                      </Button>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default SelectProjectForm;
